/**
 * use-cursor-hide — auto-hide the cursor after 3 seconds of no pointer movement.
 *
 * Only active in TV mode (spec §2.4: "Cursor hidden after 3s of no movement").
 * Uses a CSS class on `document.body` rather than inline style so the effect
 * is a single DOM write that avoids React re-renders.
 *
 * Reduced-motion: motion rules are irrelevant here (cursor visibility is not an
 * animation), but we keep the effect isolated so it can be disabled by env if needed.
 *
 * Cleanup: restores the cursor and removes the event listener on unmount.
 */
import { useEffect } from 'react';

const HIDE_CLASS = 'tv-cursor-hidden';
const HIDE_DELAY_MS = 3_000;

export function useCursorHide(active: boolean): void {
  useEffect(() => {
    if (!active) return;

    let timer: ReturnType<typeof setTimeout> | null = null;

    function hideCursor(): void {
      document.body.classList.add(HIDE_CLASS);
    }

    function showCursor(): void {
      document.body.classList.remove(HIDE_CLASS);
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(hideCursor, HIDE_DELAY_MS);
    }

    // Start the hide timer immediately (TV might never get mouse movement).
    timer = setTimeout(hideCursor, HIDE_DELAY_MS);
    window.addEventListener('pointermove', showCursor, { passive: true });

    return () => {
      if (timer !== null) clearTimeout(timer);
      window.removeEventListener('pointermove', showCursor);
      document.body.classList.remove(HIDE_CLASS);
    };
  }, [active]);
}
